import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

export const FAQ_DISCORD_INVITE_URL = 'https://discord.com/invite/gt56cQ4nWb';

export type FaqCategory = 'general' | 'sessions' | 'voice' | 'notifications' | 'account' | 'app';

export type FaqEntry = {
  /** Stable id - also the i18n catalog key, so translations survive refactors. */
  id: string;
  category: FaqCategory;
  question: MessageDescriptor;
  answer: MessageDescriptor;
  /** Optional CTA rendered under the answer (e.g. deep link to settings). */
  link?: { label: MessageDescriptor; href: string };
  /** Show this entry in the condensed FAQ block on the landing page. */
  showOnLanding?: boolean;
  /** Hide on game landing pages (a game-specific question supersedes it there). */
  hideOnGamePages?: boolean;
};

/**
 * Single source of truth for every FAQ answer on the site.
 *
 * - The standalone `/faq` page renders the full list, grouped by category.
 * - The landing FAQ block renders the `showOnLanding` subset.
 *
 * Marketing entries reuse their original `v2.faq.*` / `v3.faq.*` catalog ids so
 * the existing EN/RU translations keep working. Everything else uses `faq.*`
 * ids and is grounded in the actual product behaviour.
 */
export const FAQ_ITEMS: FaqEntry[] = [
  // ── General ────────────────────────────────────────────────────────────────
  {
    id: 'v2.faq.0.question',
    category: 'general',
    showOnLanding: true,
    hideOnGamePages: true,
    question: msg({ id: 'v2.faq.0.question', message: 'What is Teamplay.gg?' }),
    answer: msg({
      id: 'v2.faq.0.answer',
      message:
        'Teamplay is a free LFG (Looking For Group) platform that helps gamers find teammates for any game. Create a post describing what you are looking for, or browse existing posts to find your perfect squad.',
    }),
  },
  {
    id: 'v3.faq.discord.question',
    category: 'general',
    showOnLanding: true,
    question: msg({
      id: 'v3.faq.discord.question',
      message: 'How is Teamplay different from Discord LFG servers?',
    }),
    answer: msg({
      id: 'v3.faq.discord.answer',
      message:
        'On Discord your “LFG” message is buried in a wall of chat within seconds. Teamplay matches you by game, rank, platform and schedule, so the right teammates actually find you - and voice is right here, so you can talk without leaving the session. Keep your Discord server for your community; let Teamplay do the finding.',
    }),
  },
  {
    id: 'v2.faq.4.question',
    category: 'general',
    showOnLanding: true,
    // The game-specific "How do I find <Game> teammates?" (injected by the landing FAQ)
    // supersedes this generic one on game pages - hide it there to avoid a near-duplicate.
    hideOnGamePages: true,
    question: msg({ id: 'v2.faq.4.question', message: 'How do I find teammates?' }),
    answer: msg({
      id: 'v2.faq.4.answer',
      message:
        'You can either create your own LFG post describing what you are looking for (game, rank, schedule), or browse existing posts and respond to players who match your criteria. Most users find a squad within minutes.',
    }),
  },
  {
    id: 'v2.faq.1.question',
    category: 'general',
    showOnLanding: true,
    question: msg({ id: 'v2.faq.1.question', message: 'Is Teamplay free?' }),
    answer: msg({
      id: 'v2.faq.1.answer',
      message:
        'Yes, Teamplay is completely free to use. No credit card required. Create an account, find teammates, and start playing.',
    }),
  },
  {
    id: 'v2.faq.2.question',
    category: 'general',
    showOnLanding: true,
    question: msg({ id: 'v2.faq.2.question', message: 'Does Teamplay work with Discord?' }),
    answer: msg({
      id: 'v2.faq.2.answer',
      message:
        'Yes. You do not need it to talk - voice runs on Teamplay itself - but the integration is there: a session can use a Discord voice channel instead, and our bot brings LFG posts straight into your own server.',
    }),
  },
  {
    id: 'v2.faq.3.question',
    category: 'general',
    showOnLanding: true,
    question: msg({ id: 'v2.faq.3.question', message: 'Which games are supported?' }),
    answer: msg({
      id: 'v2.faq.3.answer',
      message:
        'We support 17+ games including Valorant, CS2, Fortnite, Apex Legends, League of Legends, Dota 2, Overwatch 2, PUBG, and more. New games are added regularly based on community demand.',
    }),
  },
  {
    id: 'faq.general.post',
    category: 'general',
    question: msg({
      id: 'faq.general.post.question',
      message: 'What is an LFG post and how do I create one?',
    }),
    answer: msg({
      id: 'faq.general.post.answer',
      message:
        'An LFG post is how you tell others what you want to play. Choose a game and platform, set whether you need a mic and which languages you speak, then add a short description and any game-specific details like rank or role. You can keep one active post per game, and other players reply in the comments to team up.',
    }),
  },
  {
    id: 'faq.general.respond',
    category: 'general',
    question: msg({
      id: 'faq.general.respond.question',
      message: 'How do I respond to someone else’s post?',
    }),
    answer: msg({
      id: 'faq.general.respond.answer',
      message:
        'Open any post that fits what you are after and leave a comment to introduce yourself. Agree on rank, roles or timing right there in the thread. If the post has a scheduled session or a voice channel, you can join those directly from the same page.',
    }),
  },
  {
    id: 'faq.general.bot',
    category: 'general',
    question: msg({
      id: 'faq.general.bot.question',
      message: 'Can I add Teamplay to my own Discord server?',
    }),
    answer: msg({
      id: 'faq.general.bot.answer',
      message:
        'Yes. Server owners can add the Teamplay LFG bot and let members find groups with a simple /lfg command, right inside their community. In the admin panel you connect your server and map each Discord channel to a game, so LFG activity lands in the right place automatically.',
    }),
    link: {
      label: msg({ id: 'faq.general.bot.link', message: 'Explore the Discord LFG bot' }),
      href: '/discord-lfg-bot',
    },
  },

  // ── Scheduled sessions ─────────────────────────────────────────────────────
  {
    id: 'faq.sessions.what',
    category: 'sessions',
    question: msg({
      id: 'faq.sessions.what.question',
      message: 'What is a scheduled session?',
    }),
    answer: msg({
      id: 'faq.sessions.what.answer',
      message:
        'A scheduled session is a planned game with a set time and a limited number of slots. Instead of hoping people show up, teammates reserve a spot in advance and are notified when it is time to play. You can also start an instant “Play now” session when you want to play right away.',
    }),
  },
  {
    id: 'faq.sessions.create',
    category: 'sessions',
    question: msg({
      id: 'faq.sessions.create.question',
      message: 'How do I create a scheduled session?',
    }),
    answer: msg({
      id: 'faq.sessions.create.answer',
      message:
        'When you create a post, add a session in the final step. Choose a team size (2–7 players), a duration and a start time - anywhere from right now up to two weeks ahead. Then pick how the squad will talk: voice on Teamplay, a Discord channel, or no voice. Once published, players can start reserving their slots.',
    }),
  },
  {
    id: 'faq.sessions.join',
    category: 'sessions',
    question: msg({
      id: 'faq.sessions.join.question',
      message: 'How do I join a scheduled session?',
    }),
    answer: msg({
      id: 'faq.sessions.join.answer',
      message:
        'Find a session with an open slot and join the team to reserve your place. Some games ask you to link the matching account first (for example Riot for Valorant or Epic for Fortnite). Voice needs nothing extra - only a linked Discord if the host chose a Discord channel for that session.',
    }),
  },
  {
    id: 'faq.sessions.host',
    category: 'sessions',
    question: msg({
      id: 'faq.sessions.host.question',
      message: 'Can I manage who is on my team?',
    }),
    answer: msg({
      id: 'faq.sessions.host.answer',
      message:
        'Yes. As the host you can invite players, accept or decline requests, and remove someone from the team. You can cancel an upcoming session before it starts, or mark a live session as complete when you are done.',
    }),
  },
  {
    id: 'faq.sessions.reminder',
    category: 'sessions',
    question: msg({
      id: 'faq.sessions.reminder.question',
      message: 'Will I be reminded before a session starts?',
    }),
    answer: msg({
      id: 'faq.sessions.reminder.answer',
      message:
        'Yes. Everyone on the team gets session notifications through your chosen channels - the on-site bell, email, push or Discord DM - so no one misses the start. Voice opens about 10 minutes before the session begins, so you can gather before the first match.',
    }),
  },
  {
    id: 'faq.sessions.timezone',
    category: 'sessions',
    question: msg({
      id: 'faq.sessions.timezone.question',
      message: 'How do session times and time zones work?',
    }),
    answer: msg({
      id: 'faq.sessions.timezone.answer',
      message:
        'Session times are always shown in your own local time zone, which you can set in your profile. It is worth double-checking the time before you join so there is no confusion across regions.',
    }),
  },

  // ── Voice chat ─────────────────────────────────────────────────────────────
  // Голос на платформе — основной режим (он же по умолчанию при создании сессии), Discord
  // остаётся вариантом для тех, у кого сквад уже живёт там. Порядок статей отражает это:
  // сначала «что это и как зайти», и только потом «а если мы хотим Discord».
  {
    id: 'faq.voice.what',
    category: 'voice',
    question: msg({
      id: 'faq.voice.what.question',
      message: 'Do I need Discord to talk to my teammates?',
    }),
    answer: msg({
      id: 'faq.voice.what.answer',
      message:
        'No. Voice works right on Teamplay, in your browser - nothing to install and no account anywhere else. Every session can have a group voice room for the whole squad, and you can call any teammate one-on-one from your chat with them. Discord is still there if your squad prefers it, but it is now an option rather than a requirement.',
    }),
  },
  {
    id: 'faq.voice.enable',
    category: 'voice',
    question: msg({
      id: 'faq.voice.enable.question',
      message: 'How do I turn on voice for a session?',
    }),
    answer: msg({
      id: 'faq.voice.enable.answer',
      message:
        'When you schedule a session, pick how the squad will talk: voice on Teamplay (the default), a Discord voice channel, or no voice at all. You can change your mind while the session is still open.',
    }),
  },
  {
    id: 'faq.voice.join',
    category: 'voice',
    question: msg({
      id: 'faq.voice.join.question',
      message: 'How do I join the voice room?',
    }),
    answer: msg({
      id: 'faq.voice.join.answer',
      message:
        'Open the session page and press Join - the room opens about 10 minutes before the start time. Your browser will ask for microphone access once. Hold Space to talk if you keep your mic muted. If the session uses a Discord channel instead, you will need a linked Discord account and the host approves your request.',
    }),
  },
  {
    id: 'faq.voice.who',
    category: 'voice',
    question: msg({
      id: 'faq.voice.who.question',
      message: 'Who can join the voice room?',
    }),
    answer: msg({
      id: 'faq.voice.who.answer',
      message:
        'Only the squad: the host and players whose place in the session is confirmed. The room holds exactly as many people as there are slots in the session, so nobody random can walk in.',
    }),
  },
  {
    id: 'faq.voice.video',
    category: 'voice',
    question: msg({
      id: 'faq.voice.video.question',
      message: 'Is there video or screen sharing?',
    }),
    answer: msg({
      id: 'faq.voice.video.answer',
      message:
        'In a one-on-one call from a chat - yes: turn on your camera or share your screen to show a build, a setup or a replay. The session room is voice only, so a full squad can talk without eating your connection.',
    }),
  },
  {
    id: 'faq.voice.manage',
    category: 'voice',
    question: msg({
      id: 'faq.voice.manage.question',
      message: 'How do I manage or remove people in voice?',
    }),
    answer: msg({
      id: 'faq.voice.manage.answer',
      message:
        'The host can mute anyone for everybody or remove them from the room, and a removed player cannot come back. In a Discord channel the host approves and removes people from the management panel instead.',
    }),
  },
  {
    id: 'faq.voice.delete',
    category: 'voice',
    question: msg({
      id: 'faq.voice.delete.question',
      message: 'How do I turn voice off?',
    }),
    answer: msg({
      id: 'faq.voice.delete.answer',
      message:
        'Edit the session and set voice to off, or delete the session entirely - the room (or the Discord channel, if you used one) goes with it.',
    }),
  },

  // ── Notifications ──────────────────────────────────────────────────────────
  {
    id: 'faq.notifications.channels',
    category: 'notifications',
    question: msg({
      id: 'faq.notifications.channels.question',
      message: 'What notifications can I get, and how?',
    }),
    answer: msg({
      id: 'faq.notifications.channels.answer',
      message:
        'Teamplay keeps you up to date on replies, team invites, direct messages and upcoming sessions. You choose how they reach you:\n\n• On-site bell - always on while you are logged in.\n• Email - key updates in your inbox.\n• Browser & PWA push - real-time alerts on your device.\n• Discord DM - updates in your direct messages (requires a linked Discord account).\n\nSecurity and account messages are always delivered, no matter your settings. You can fine-tune everything else any time.',
    }),
    link: {
      label: msg({
        id: 'faq.notifications.channels.link',
        message: 'Manage notification settings',
      }),
      href: '/dashboard/settings/notifications',
    },
  },
  {
    id: 'faq.notifications.push',
    category: 'notifications',
    question: msg({
      id: 'faq.notifications.push.question',
      message: 'Why am I not receiving push notifications?',
    }),
    answer: msg({
      id: 'faq.notifications.push.answer',
      message:
        'Push works in modern browsers such as Chrome, Firefox and Edge. On iPhone you first need to install Teamplay to your home screen, then allow notifications. Make sure push is enabled in your notification settings and that your browser has permission - if you signed up with email, verify your address so updates can reach you.',
    }),
    link: {
      label: msg({
        id: 'faq.notifications.push.link',
        message: 'Open notification settings',
      }),
      href: '/dashboard/settings/notifications',
    },
  },

  // ── Account & safety ───────────────────────────────────────────────────────
  {
    id: 'faq.account.signup',
    category: 'account',
    question: msg({
      id: 'faq.account.signup.question',
      message: 'How do I sign up?',
    }),
    answer: msg({
      id: 'faq.account.signup.answer',
      message:
        'You can register with an email and password, or sign in with one click using Discord, Google, Steam, Riot or Epic Games. If you use email and password, confirm your email from the link we send - it verifies your account and unlocks email notifications.',
    }),
  },
  {
    id: 'faq.account.linking',
    category: 'account',
    question: msg({
      id: 'faq.account.linking.question',
      message: 'Why should I link my game accounts?',
    }),
    answer: msg({
      id: 'faq.account.linking.answer',
      message:
        'You can link Discord, Riot, Epic Games, Steam and Google from your settings. Linking Discord lets you use voice channels, some games require the matching account before you can join their sessions, and linked profiles help teammates trust who they are playing with.',
    }),
    link: {
      label: msg({ id: 'faq.account.linking.link', message: 'Manage linked accounts' }),
      href: '/dashboard/settings/account',
    },
  },
  {
    id: 'faq.account.profile',
    category: 'account',
    question: msg({
      id: 'faq.account.profile.question',
      message: 'What can I put on my profile?',
    }),
    answer: msg({
      id: 'faq.account.profile.answer',
      message:
        'Your profile carries a display name, avatar, short bio, the games you play, languages, interests and country. Rank and role are set per game on your posts, so teammates can see exactly how you like to play.',
    }),
  },
  {
    id: 'faq.account.messages',
    category: 'account',
    question: msg({
      id: 'faq.account.messages.question',
      message: 'Can I message other players directly?',
    }),
    answer: msg({
      id: 'faq.account.messages.answer',
      message:
        'Yes. Teamplay has built-in direct messages so you can chat one-on-one, react to messages and reply in a thread - no need to swap contacts elsewhere. If someone bothers you, you can block them right from the chat.',
    }),
  },
  {
    id: 'faq.account.safety',
    category: 'account',
    question: msg({
      id: 'faq.account.safety.question',
      message: 'How do I report or block someone?',
    }),
    answer: msg({
      id: 'faq.account.safety.answer',
      message:
        'You can report a post, a comment or a user from the menu next to it, and block anyone directly in your chat with them. We take reports seriously and moderate accounts that break the rules.',
    }),
  },

  // ── App / PWA ──────────────────────────────────────────────────────────────
  {
    id: 'faq.app.install',
    category: 'app',
    question: msg({
      id: 'faq.app.install.question',
      message: 'How do I install Teamplay as an app (PWA)?',
    }),
    answer: msg({
      id: 'faq.app.install.answer',
      message:
        'You can install Teamplay on your device like a normal app - no app store needed. It works in most modern browsers and gives you home-screen access, push notifications and a faster experience.\n\n• Android (Chrome, Edge): open teamplay.gg, tap the menu (⋮) and choose “Add to Home screen”.\n• iPhone (Safari): open teamplay.gg in Safari, tap Share, then “Add to Home Screen”.\n• Desktop (Chrome, Edge): click the install icon in the address bar and confirm.',
    }),
  },
];

export type FaqCategoryOption = { value: FaqCategory | 'all'; label: MessageDescriptor };

/** Filter tabs for the standalone FAQ page, in display order. */
export const FAQ_CATEGORIES: FaqCategoryOption[] = [
  { value: 'all', label: msg({ id: 'faq.category.all', message: 'All' }) },
  { value: 'general', label: msg({ id: 'faq.category.general', message: 'General' }) },
  { value: 'sessions', label: msg({ id: 'faq.category.sessions', message: 'Sessions' }) },
  { value: 'voice', label: msg({ id: 'faq.category.voice', message: 'Voice chat' }) },
  {
    value: 'notifications',
    label: msg({ id: 'faq.category.notifications', message: 'Notifications' }),
  },
  { value: 'account', label: msg({ id: 'faq.category.account', message: 'Account & safety' }) },
  { value: 'app', label: msg({ id: 'faq.category.app', message: 'App' }) },
];
